/* kns-mobile-chat-access
 *
 * Mobile-only Chatwoot entry points for K&S, loaded by layout/theme.liquid:
 *   <script src="..." defer data-kns-mobile-chat-access
 *           data-menu-label="..." data-product-label="..."
 *           data-unread-label="..." data-contact-href="/pages/contact-us">
 *
 * Contracts (live theme):
 * - Open with window.$chatwoot.toggle('open'). If the SDK is missing or
 *   toggle throws, do nothing else: the anchor's normal contact-href
 *   navigation must work, so preventDefault is called ONLY after a
 *   successful toggle call.
 * - Unread counts arrive on chatwoot:on-unread-message-count-changed as
 *   e.detail.unreadMessageCount (NOT detail.count).
 * - #cw-widget-holder always keeps its iframe; open/closed is signalled by
 *   removing/adding the `woot--hide` class. On open->closed we return focus
 *   to the link that opened the widget, but only while focus is still on
 *   body or inside the holder/iframe; we never steal focus from an outside
 *   control.
 *
 * Adds (visible <=768px only, hidden on desktop):
 * 1. An <li> in ul.main-nav whose anchor carries the theme's own
 *    main-nav__item main-nav__item--primary classes; the LI itself is
 *    unstyled, the anchor is a full-width block row (>=44px).
 * 2. One anchor immediately after each <product-form> wrapping
 *    form.js-product-form-main (outside the form; <template> skipped).
 *
 * html.kns-mobile-chat-access-ready exists exactly while the menu anchor
 * exists (that CSS, owned by the parent, hides the desktop float at mobile
 * widths). Idempotent across re-execution and section rerenders;
 * window.KnsMobileChatAccess.refresh() re-scans without duplicating
 * elements or listeners. Google widget untouched.
 */
(function (window, document) {
  "use strict";

  if (window.KnsMobileChatAccess && window.KnsMobileChatAccess.__knsMobileChatAccessBuilt) {
    return;
  }
  var api = { __knsMobileChatAccessBuilt: true };

  var MENU_SELECTOR = "a.kns-mobile-chat-menu";
  var PRODUCT_SELECTOR = "a.kns-mobile-chat-product";
  var ENTRY_SELECTOR = ".kns-mobile-chat-entry";
  var READY_CLASS = "kns-mobile-chat-access-ready";
  var STYLE_ID = "kns-mobile-chat-access-style";

  var sourceScript = document.currentScript;
  var ds = (sourceScript && sourceScript.dataset) || {};
  var menuLabel = ds.menuLabel || "Chat with us";
  var productLabel = ds.productLabel || "Ask about this item";
  var unreadLabel = ds.unreadLabel || "unread messages";
  var contactHref = ds.contactHref || "/pages/contact-us";
  var latestUnreadCount = 0;
  // Nodes this script inserted itself; clones made by the theme are not in it.
  var ownNodes = typeof WeakSet === "function" ? new WeakSet() : null;

  function ensureStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = [
      ".kns-mobile-chat-entry{display:none;box-sizing:border-box;",
      "min-height:44px;padding:10px 12px;color:inherit;text-decoration:none;}",
      ".kns-mobile-chat-entry:focus-visible{outline:2px solid currentColor;outline-offset:2px}",
      ".kns-mobile-chat-entry__count{display:none;min-width:20px;margin-inline-start:6px;padding:1px 6px;",
      "border-radius:999px;background:rgb(var(--color-accent,178,20,20));",
      "color:rgb(var(--color-base,255,255,255));font-size:0.75rem;font-weight:600;",
      "text-align:center}",
      ".kns-mobile-chat-entry[data-unread-count] .kns-mobile-chat-entry__count{display:inline-block}",
      "a.kns-mobile-chat-product{margin:12px 0}",
      "@media (max-width:768px){.kns-mobile-chat-entry{display:block}}"
    ].join("\n");
    document.head.appendChild(style);
  }

  function bindClick(link) {
    if (!link.__knsChatBound) {
      link.__knsChatBound = true;
      link.addEventListener("click", onClick);
    }
  }

  function makeEntry(className, label) {
    var a = document.createElement("a");
    a.className = className;
    a.href = contactHref;
    a.dataset.knsChatBaseLabel = label;
    a.textContent = label;
    var badge = document.createElement("span");
    badge.className = "kns-mobile-chat-entry__count";
    badge.setAttribute("aria-hidden", "true");
    a.appendChild(badge);
    bindClick(a);
    if (ownNodes) ownNodes.add(a);
    return a;
  }

  function setUnread(link, count) {
    if (!link) return;
    var badge = link.querySelector(".kns-mobile-chat-entry__count");
    if (!badge) return;
    var n = parseInt(count, 10) || 0;
    // Write only on difference: rewriting badge text replaces its text node,
    // a body childList mutation that would reschedule refresh forever.
    if (n > 0) {
      var text = String(n);
      var label = link.dataset.knsChatBaseLabel + " " + n + " " + unreadLabel;
      if (link.getAttribute("data-unread-count") !== text) link.setAttribute("data-unread-count", text);
      if (badge.textContent !== text) badge.textContent = text;
      if (link.getAttribute("aria-label") !== label) link.setAttribute("aria-label", label);
    } else {
      if (link.hasAttribute("data-unread-count")) link.removeAttribute("data-unread-count");
      if (badge.firstChild) badge.textContent = "";
      if (link.hasAttribute("aria-label")) link.removeAttribute("aria-label");
    }
  }

  function onUnread(event) {
    var detail = (event && event.detail) || {};
    latestUnreadCount = parseInt(detail.unreadMessageCount, 10) || 0;
    var entries = document.querySelectorAll(ENTRY_SELECTOR);
    for (var i = 0; i < entries.length; i++) setUnread(entries[i], latestUnreadCount);
  }

  // ---- widget open + focus restoration ------------------------------------
  var activatingEntry = null;   // link waiting for its open->closed transition
  var wasOpen = false;          // the transition must include an open state
  var widgetObserver = null;

  function holder() {
    return document.getElementById("cw-widget-holder");
  }

  function widgetOpen() {
    var el = holder();
    return !!el && !el.classList.contains("woot--hide");
  }

  function focusInsideWidget() {
    var active = document.activeElement;
    var el = holder();
    if (!active || !el) return false;
    return active === document.body || active === el || el.contains(active);
  }

  function installWidgetObserver() {
    if (widgetObserver || typeof MutationObserver === "undefined") return;
    var el = holder();
    if (!el) return;
    widgetObserver = new MutationObserver(function () {
      if (!activatingEntry) return;
      if (widgetOpen()) {
        wasOpen = true;
        return;
      }
      if (!wasOpen) return;
      if (focusInsideWidget()) activatingEntry.focus();
      activatingEntry = null;
      wasOpen = false;
    });
    widgetObserver.observe(el, { attributes: true, attributeFilter: ["class"] });
  }

  function onClick(event) {
    var sdk = window.$chatwoot;
    if (!sdk || typeof sdk.toggle !== "function") return; // href navigates
    var ok;
    try {
      ok = sdk.toggle("open");
    } catch (err) {
      ok = false;
    }
    if (ok === false) return;        // toggle failed: let the href navigate
    event.preventDefault();          // only after a successful open
    activatingEntry = this;
    wasOpen = widgetOpen();
    installWidgetObserver();
  }

  // ---- insertions -----------------------------------------------------------
  function findMenuUl() {
    return document.querySelector("main-menu .main-menu__content ul.main-nav") ||
      document.querySelector("main-menu ul.main-nav");
  }

  function syncMenuLink() {
    var ul = findMenuUl();
    var link = document.querySelector(MENU_SELECTOR);
    if (link && (!ul || !ul.contains(link))) {
      (link.closest("li") || link).remove();  // menu rebuilt: drop the orphan
      link = null;
    }
    if (!ul) return false;
    if (link) {
      bindClick(link);
      return true;
    }
    var li = document.createElement("li");
    if (ownNodes) ownNodes.add(li);
    var a = makeEntry(
      "kns-mobile-chat-entry kns-mobile-chat-menu main-nav__item main-nav__item--primary",
      menuLabel);
    li.appendChild(a);
    ul.appendChild(li);
    return true;
  }

  function syncProductLinks() {
    var forms = document.querySelectorAll(
      "product-form[data-product-id] form.js-product-form-main");
    var wanted = [];
    for (var i = 0; i < forms.length; i++) {
      if (forms[i].closest("template")) continue;
      wanted.push(forms[i].closest("product-form") || forms[i]);
    }
    var links = document.querySelectorAll(PRODUCT_SELECTOR);
    for (i = 0; i < links.length; i++) {
      var owner = links[i].previousElementSibling;
      var slot = wanted.indexOf(owner);
      if (slot === -1) {
        links[i].remove();                    // orphan/clone: dedupe
      } else {
        wanted.splice(slot, 1);               // already satisfied
        bindClick(links[i]);
      }
    }
    for (i = 0; i < wanted.length; i++) {
      wanted[i].insertAdjacentElement("afterend",
        makeEntry("kns-mobile-chat-entry kns-mobile-chat-product", productLabel));
    }
  }

  function syncReadyClass() {
    document.documentElement.classList
      .toggle(READY_CLASS, Boolean(document.querySelector(MENU_SELECTOR)));
  }

  // ---- lifecycle ------------------------------------------------------------
  var refreshTimer = null;
  function scheduleRefresh() {
    if (refreshTimer) return;
    refreshTimer = setTimeout(function () {
      refreshTimer = null;
      api.refresh();
    }, 60);
  }

  // A record is script-owned when it happens inside one of our entries (badge
  // text) or only adds nodes this script created. Removals always count, so
  // theme rerenders that drop an entry are still repaired.
  function ownedRecord(record) {
    var target = record.target;
    var el = target && target.nodeType === 1 ? target : target && target.parentNode;
    if (el && el.closest && el.closest(ENTRY_SELECTOR)) return true;
    if (!ownNodes || record.removedNodes.length || !record.addedNodes.length) return false;
    for (var i = 0; i < record.addedNodes.length; i++) {
      if (!ownNodes.has(record.addedNodes[i])) return false;
    }
    return true;
  }

  function onBodyMutations(records) {
    for (var i = 0; i < records.length; i++) {
      if (!ownedRecord(records[i])) {
        scheduleRefresh();
        return;
      }
    }
  }

  function refresh() {
    ensureStyle();
    syncMenuLink();
    syncProductLinks();
    syncReadyClass();
    var entries = document.querySelectorAll(ENTRY_SELECTOR);
    for (var i = 0; i < entries.length; i++) setUnread(entries[i], latestUnreadCount);
  }
  api.refresh = refresh;
  window.KnsMobileChatAccess = api;

  function boot() {
    refresh();
    window.addEventListener("chatwoot:on-unread-message-count-changed", onUnread);
    if (typeof MutationObserver !== "undefined") {
      new MutationObserver(onBodyMutations).observe(document.body, {
        childList: true, subtree: true
      });
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }
})(window, document);
